import type { SerieTemporalGranularidad } from '../types'

/** Granularidad de las gráficas de serie temporal (día / semana / mes). */
export type Granularidad = SerieTemporalGranularidad

export const GRAN_LABEL: Record<Granularidad, string> = {
  dia: 'Diario',
  semana: 'Semanal',
  mes: 'Mensual',
}

/** Sufijo para etiquetas de promedios por periodo (ej. "ventas/día"). */
export const GRAN_SUFIJO: Record<Granularidad, string> = {
  dia: 'día',
  semana: 'semana',
  mes: 'mes',
}

/** Etiqueta del total mostrado en el chart según granularidad (día a día…). */
export const GRAN_RITMO: Record<Granularidad, string> = {
  dia: 'día a día',
  semana: 'semana a semana',
  mes: 'mes a mes',
}

/** Inicio de semana ISO (lunes) en formato 'YYYY-MM-DD', para bucketing local. */
function inicioSemanaISO(iso: string): string {
  const d = new Date(iso + 'T12:00:00')
  const dow = (d.getDay() + 6) % 7 // 0 = lunes
  d.setDate(d.getDate() - dow)
  return d.toISOString().slice(0, 10)
}

/** Parse seguro de 'YYYY-MM' o 'YYYY-MM-DD' a Date local (mediodía). */
function parseFechaLocal(s: string): Date {
  if (/^\d{4}-\d{2}$/.test(s)) return new Date(s + '-01T12:00:00')
  return new Date(s + 'T12:00:00')
}

/** "7 sep" (día + mes abreviado, sin punto). */
export function formatDiaMes(d: Date): string {
  return d.toLocaleDateString('es-EC', { day: 'numeric', month: 'short' }).replace(/\./g, '')
}

/** Rango de la semana que empieza en `s` (YYYY-MM-DD): "7 sep – 13 sep". */
export function rangoSemana(s: string): string {
  const ini = parseFechaLocal(s)
  const fin = new Date(ini)
  fin.setDate(fin.getDate() + 6)
  return `${formatDiaMes(ini)} – ${formatDiaMes(fin)}`
}

/** Etiqueta legible de un periodo según granularidad (para cards/ejes). */
export function etiquetaPeriodo(fecha: string, gran: Granularidad): string {
  if (gran === 'semana') return rangoSemana(fecha)
  if (gran === 'mes') {
    return parseFechaLocal(fecha).toLocaleDateString('es-EC', { month: 'long', year: 'numeric' })
  }
  return formatDiaMes(parseFechaLocal(fecha))
}

/**
 * Clave de bucket de una fecha diaria ('YYYY-MM-DD') según la granularidad:
 * el día tal cual, el lunes de su semana, o su mes ('YYYY-MM'). Permite agrupar
 * client-side varias series por el mismo criterio (curvas acumuladas alineadas).
 */
export function claveGranularidad(fecha: string, gran: Granularidad): string {
  if (gran === 'mes') return fecha.slice(0, 7)
  if (gran === 'semana') return inicioSemanaISO(fecha)
  return fecha
}

/**
 * Reagrupa una serie diaria (`fecha` = 'YYYY-MM-DD') a la granularidad dada,
 * sumando `total` y `secondary`. Se usa para las series derivadas client-side
 * (path de producto), que no pasan por la agregación por periodo del backend.
 * En 'dia' devuelve las filas tal cual (normalizando `secondary`).
 */
export function agruparSerieFecha(
  rows: Array<{ fecha: string; total: number; secondary?: number }>,
  gran: Granularidad,
): Array<{ fecha: string; total: number; secondary: number }> {
  if (gran === 'dia') {
    return rows.map((r) => ({ fecha: r.fecha, total: r.total, secondary: r.secondary ?? 0 }))
  }
  const clave = (f: string) => (gran === 'mes' ? f.slice(0, 7) : inicioSemanaISO(f))
  const buckets = new Map<string, { total: number; secondary: number }>()
  for (const r of rows) {
    const k = clave(r.fecha)
    const prev = buckets.get(k) ?? { total: 0, secondary: 0 }
    prev.total += r.total
    prev.secondary += r.secondary ?? 0
    buckets.set(k, prev)
  }
  return Array.from(buckets, ([fecha, v]) => ({ fecha, total: v.total, secondary: v.secondary })).sort(
    (a, b) => a.fecha.localeCompare(b.fecha),
  )
}
